import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Switch, ViewStyle } from 'react-native';
import { ChevronRight, type LucideIcon } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER, switchColors } from '../../utils/webStyles';

interface ListItemProps {
  icon: LucideIcon;
  label: string;
  onPress?: () => void;
  badge?: string;
  variant?: 'default' | 'danger';
  /** Display a string value (e.g. "System") with a chevron */
  value?: string;
  /** Render a toggle switch instead of chevron */
  toggle?: boolean;
  toggleValue?: boolean;
  onToggle?: (value: boolean) => void;
  /** Show bottom divider */
  showDivider?: boolean;
  disabled?: boolean;
  style?: ViewStyle;
  testID?: string;
}

export function ListItem({
  icon: Icon,
  label,
  onPress,
  badge,
  variant = 'default',
  value,
  toggle,
  toggleValue,
  onToggle,
  showDivider = false,
  disabled,
  style,
  testID,
}: ListItemProps) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover(Boolean(onPress) && !disabled && !toggle);

  const isDanger = variant === 'danger';
  const iconColor = isDanger ? colors.danger : colors.textSecondary;

  return (
    <TouchableOpacity
      {...hoverProps}
      testID={testID}
      style={[
        styles.container,
        { borderBottomColor: showDivider ? colors.divider : 'transparent' },
        hovered && { backgroundColor: colors.hoverSurface },
        Boolean(onPress) && !disabled && !toggle && CURSOR_POINTER,
        style,
      ]}
      onPress={onPress}
      disabled={disabled || toggle}
      // @ts-expect-error dataSet is a react-native-web prop (drives the
      // press-scale rule in motion.ts's MOTION_CSS_VARS); not in RN's type
      // defs.
      dataSet={{ rediPress: 'true' }}
      accessibilityRole={toggle ? 'switch' : 'button'}
      accessibilityLabel={label}
      accessibilityState={{
        disabled: disabled || toggle,
        ...(toggle ? { checked: toggleValue } : {}),
      }}
      activeOpacity={0.7}>
      <Icon size={18} color={iconColor} style={styles.icon} />

      <Text style={[styles.label, { color: isDanger ? colors.danger : colors.text }]}>{label}</Text>

      {badge && (
        <Text
          style={[styles.badgeText, { color: colors.textSecondary }]}
          // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
          // 'wide': badges show live counts whose digit count changes the box width.
          dataSet={{ visualmask: 'wide' }}>
          {badge}
        </Text>
      )}

      {toggle ? (
        <Switch
          value={toggleValue}
          onValueChange={onToggle}
          // Ink track, inverse thumb: a switch is an action control, and
          // green here reads as "progress" on a row that has nothing to do
          // with readiness. switchColors also kills RN-web's teal ON thumb.
          {...switchColors(colors)}
          accessibilityLabel={`${label} toggle`}
        />
      ) : value ? (
        <View style={styles.valueContainer}>
          <Text style={[styles.valueText, { color: colors.textSecondary }]}>{value}</Text>
          <ChevronRight size={18} color={colors.textTertiary} />
        </View>
      ) : (
        <ChevronRight size={18} color={colors.textTertiary} />
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    paddingHorizontal: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  // Bare icon — no tinted container box; the row's meaning lives in its
  // label, and identical green boxes on every row collapse the hierarchy.
  icon: {
    marginRight: SPACING.smd,
  },
  label: {
    ...TYPOGRAPHY.bodyLarge,
    flex: 1,
  },
  // A count is metadata, not a status: it renders as the same gray
  // label/value text the `value` slot uses, never as a green chip. Green is
  // reserved for category and readiness.
  badgeText: {
    ...TYPOGRAPHY.body,
    marginRight: SPACING.xs,
  },
  valueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  valueText: {
    ...TYPOGRAPHY.body,
    marginRight: SPACING.xs,
  },
});
